import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { useRouter } from 'next/router'
import { useDictionary } from '@/lib/i18n'
import { siteConfig } from '@/constants/site'

const OG_LOCALES: Record<string, string> = {
    'en': 'en_US',
    'zh-tw': 'zh_TW'
}

export const Seo: FC<SeoProps> = ({
    title,
    description,
    ogImage = siteConfig.ogImage
}): ReactNode => {
    const { locale = 'en', locales = [], defaultLocale = 'en', pathname } = useRouter()
    const t = useDictionary()

    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const pageDescription = description ?? t.meta.description
    const path = pathname === '/' ? '' : pathname

    const localeUrl = (code: string): string => {
        const prefix = code === defaultLocale ? '' : `/${code}`

        return `${siteConfig.url}${prefix}${path}` || '/'
    }

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={pageDescription} />
            <link rel='canonical' href={localeUrl(locale)} key='canonical' />

            {locales.map((code) => (
                <link key={`alternate-${code}`} rel='alternate' hrefLang={code} href={localeUrl(code)} />
            ))}
            <link key='alternate-default' rel='alternate' hrefLang='x-default' href={localeUrl(defaultLocale)} />

            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={pageDescription} />
            <meta property='og:type' content='website' />
            <meta property='og:url' content={localeUrl(locale)} />
            <meta property='og:site_name' content={siteConfig.name} />
            <meta property='og:locale' content={OG_LOCALES[locale] ?? 'en_US'} />

            {locales.filter((code) => code !== locale).map((code) => (
                <meta key={`og-locale-${code}`} property='og:locale:alternate' content={OG_LOCALES[code] ?? code} />
            ))}

            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={pageDescription} />

            {ogImage && <meta property='og:image' content={ogImage} />}
            {ogImage && <meta property='og:image:width' content='1200' />}
            {ogImage && <meta property='og:image:height' content='675' />}
            {ogImage && <meta name='twitter:image' content={ogImage} />}
        </Head>
    )
}
